// @flow
import React, { PureComponent } from 'react';
import { ScrollView, StyleSheet, View, RefreshControl } from 'react-native';
import { Separator } from 'trpc-react-native-ui';

import PendingDeliveryPanel from '../PendingDeliveryPanel.container';
import { PlanSummaryFrame } from '../../../../components/plan';
import { FullButton, FlatList } from '../../../../components/common';
import I18n from '../../../../lib/i18n';
import NoPendingDeliveryPanel from '../NoPendingDeliveryPanel.container';

import type { Subscription } from 'digicel-kaizen-library';
import type { DeliveryResourceType } from '../../../../modules/DeliveryResource/reducer';
import { WalletForfeitureCountdown } from '../../../../components/common/WalletForfeitureCountdown';
import PaymentAuthentications from '../../../../components/payments/PaymentAuthentications';

export default class extends PureComponent<Props> {
  componentDidMount() {
    this.props.fetchWalletForfeitureData();
  }

  onRefresh = () => {
    this.props.fetchWalletForfeitureData();
  };

  styles = getStyles();

  deliveryResourceKeyExtractor = (deliveryResource: DeliveryResourceType) => deliveryResource.ouid;
  planSummaryKeyExtractor = (subscription: Subscription) => subscription.ouid;

  renderDeliveryResource = ({ item }: { item: DeliveryResourceType }) => (
    <PendingDeliveryPanel deliveryResource={item} style={this.styles.separatorMargin} />
  );

  renderPlanSummary = ({ item }: { item: Subscription }) => (
    <PlanSummaryFrame
      key={item.ouid}
      subscription={item}
      showTitle={!item.hasUnavailableOffers}
      isActivation={
        this.props.subscriptionsToActivateWithPlan && this.props.subscriptionsToActivateWithPlan[0].portability
      }
      style={this.styles.separatorMargin}
      unavailablePlanText={I18n.t('Dashboard.Plan.unavailablePlanOnDashboard')}
      fromDashboard
    />
  );

  renderWalletForfeitureCountdown = (separatorProps?: Object) =>
    !!this.props.dateToCallWalletForfeiture &&
    !!this.props.walletBalance && (
      <>
        <WalletForfeitureCountdown key={'renderWalletForfeitureCountdownKey'} timestamp={this.props.dateToCallWalletForfeiture} />
        <Separator key={'renderWalletForfeitureCountdownSeparatorKey'} crossAxisMargin={-3} {...separatorProps} />
      </>
    );

  render() {
    const styles = this.styles;

    const refreshControl = <RefreshControl onRefresh={this.onRefresh} refreshing={this.props.isLoading} />;

    if (!this.props.deliveryResources.length && !this.props.subscriptionsToActivateWithPlan.length) {
      return (
        <ScrollView refreshControl={refreshControl}>
          {this.renderWalletForfeitureCountdown({ thickness: 4 })}
          <NoPendingDeliveryPanel key={'noPendingDeliveryPanelKey'} />
        </ScrollView>
      );
    }

    return (
      <View style={styles.container}>
        <ScrollView refreshControl={refreshControl} contentContainerStyle={styles.contentContainerStyle}>
          <PaymentAuthentications key={'paymentAuthenticationsKey'}/>
          <View>
            <FlatList
              scrollEnabled={false}
              key={'deliveryResourcesList'}
              data={this.props.deliveryResources}
              ListHeaderComponent={this.renderWalletForfeitureCountdown}
              renderItem={this.renderDeliveryResource}
              keyExtractor={this.deliveryResourceKeyExtractor}
              extraData={{ now: Math.trunc(Date.now() / 15000) }} // date is necessary to rerender "time ago" (15sec delay)
            />
          </View>
          <FlatList
            scrollEnabled={false}
            key={'subscriptionsToActivateWithPlanList'}
            data={this.props.subscriptionsToActivateWithPlan}
            renderItem={this.renderPlanSummary}
            keyExtractor={this.planSummaryKeyExtractor}
          />
        </ScrollView>
        <View style={styles.activateButtonContainer}>
          <FullButton text={I18n.t('Dashboard.Orders.activateSim')} onPress={this.props.goToSimActivation} />
        </View>
      </View>
    );
  }
}

type Props = DispatchProps & StateProps;

export type StateProps = {
  dateToCallWalletForfeiture: ?string,
  deliveryResources: Array<DeliveryResourceType>,
  isLoading: boolean,
  subscriptionsToActivateWithPlan: Array<Subscription>,
  walletBalance?: number,
};

export type DispatchProps = {
  fetchDashboardData: Function,
  fetchWalletForfeitureData: Function,
  goToSimActivation: Function,
};

const getStyles = () =>
  StyleSheet.create({
    contentContainerStyle: {
      paddingBottom: 62,
    },
    activateButtonContainer: {
      position: 'absolute',
      bottom: 10,
      width: '100%',
    },
    container: {
      flex: 1,
    },
    separatorMargin: {
      marginVertical: 3,
    },
  });
